import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { SS_ROW_ACTIONS, buildForceMovePlan, formatSsRowActionLabel } from '../constants/jobModel';
import { daysUntilDue } from '../utils/dueDate';
import {
  captureWindowScroll,
  pinWindowScroll,
  preventFocusScroll,
} from '../utils/windowScrollGuard';

function invalidateDueQueries(queryClient, jobId) {
  // Prefix invalidation covers ['jobs', ...] — avoid stacking duplicate refetches
  queryClient.invalidateQueries({ queryKey: ['jobs'] });
  if (jobId != null) queryClient.invalidateQueries({ queryKey: ['job', String(jobId)] });
  queryClient.invalidateQueries({ queryKey: ['buildings', 'overdue'] });
  queryClient.invalidateQueries({ queryKey: ['buildings', 'due-soon'] });
  queryClient.invalidateQueries({ queryKey: ['job-schedule-events'] });
  queryClient.invalidateQueries({ queryKey: ['schedules'] });
}

function toYmd(iso) {
  if (!iso) return null;
  const s = String(iso).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${day}`;
  } catch {
    return null;
  }
}

/** Which action button matches the live job / worker-driven status. */
export function selectedSsActionValue(status, days) {
  const st = String(status || '').toUpperCase();
  if (st === 'NOTIFIED') return 'email';
  if (st === 'SCHEDULED' || st === 'ACCEPTED' || st === 'WAITING') return 'schedule';
  if (st === 'IN_PROGRESS' || st === 'REPAIRING' || st === 'AFTER_PAPERWORK') return 'force_IN_PROGRESS';
  if (st === 'COMPLETED') return 'force_COMPLETED';
  if (days != null && days < 0) return 'force_OVERDUE';
  if (days != null && days >= 0 && days <= 30) return 'force_URGENT';
  return '';
}

/**
 * Per-SS action buttons — click runs immediately (no Confirm).
 * Selected button is shaded from current status (admin or worker updates).
 */
export default function ForceMoveControl({
  jobId: jobIdProp,
  buildingId,
  dueDate,
  scheduledDate,
  buildingName,
  ssCode,
  ssName,
  bucket,
  days,
  scheduleDate,
  currentStatus,
  compact = false,
  onMoved,
  onScheduleRequest,
  className = '',
}) {
  const queryClient = useQueryClient();
  const [pendingValue, setPendingValue] = useState('');
  const daysVal = days != null ? days : daysUntilDue(dueDate);
  const selected = selectedSsActionValue(currentStatus, daysVal);

  const ensureJobId = async () => {
    if (jobIdProp) return jobIdProp;
    if (!buildingId || !ssCode) {
      throw new Error('Missing building or SS — cannot create inspection');
    }
    const due = toYmd(dueDate);
    if (!due) throw new Error('Missing due date — cannot create inspection');
    const res = await api.post('/jobs', {
      buildingId,
      ssCode,
      ssName: ssName || ssCode,
      dueDate: due,
      inspectionType: 'ANNUAL',
      inspectionFrequency: 'ANNUAL',
    });
    const id = res.data?.id;
    if (!id) throw new Error('Failed to create inspection');
    toast.success(`Inspection #${id} created for ${ssCode}`);
    return id;
  };

  const mutation = useMutation({
    mutationFn: async (actionValue) => {
      if (actionValue === 'email') {
        const jobId = jobIdProp || (await ensureJobId().catch(() => null));
        const res = await api.post('/jobs/notify-urgent', {
          items: [
            {
              building_name: buildingName,
              ss_code: ssCode,
              due_date: dueDate,
              job_id: jobId || null,
              bucket: bucket || undefined,
              days: daysVal,
            },
          ],
        });
        if (jobId) {
          await api.patch(`/jobs/${jobId}`, {
            status: 'NOTIFIED',
            admin_note: 'Mail action — status Mail (NOTIFIED)',
          });
        }
        return { kind: 'email', data: res.data, jobId };
      }

      if (actionValue === 'schedule' || actionValue === 'force_BOOKED') {
        if (typeof onScheduleRequest === 'function') {
          return { kind: 'schedule_ui', jobId: jobIdProp || null };
        }
        const jobId = await ensureJobId();
        const date =
          scheduleDate ||
          (() => {
            const d = new Date();
            d.setDate(d.getDate() + 1);
            return d.toISOString().slice(0, 10);
          })();
        const res = await api.post('/jobs/bulk-schedule', {
          jobIds: [jobId],
          scheduled_date: date,
        });
        return { kind: 'schedule', data: res.data, jobId };
      }

      if (actionValue.startsWith('force_')) {
        const jobId = await ensureJobId();
        const forceValue = actionValue.replace(/^force_/, '');
        const plan = buildForceMovePlan(forceValue, {
          due_date: dueDate,
          scheduled_date: scheduledDate,
          daysUntil: daysVal,
        });
        if (!plan) throw new Error('Unknown move target');
        if (plan.patch) {
          const res = await api.patch(`/jobs/${jobId}`, plan.patch);
          return {
            kind: 'force',
            data: res.data,
            jobId,
            label: plan.label,
            forceValue,
          };
        }
        return {
          kind: 'force',
          data: null,
          jobId,
          label: plan.label,
          forceValue,
        };
      }

      throw new Error('Unknown action');
    },
    onError: (error) => {
      const data = error.response?.data;
      const msg =
        (typeof data === 'string' && data) ||
        data?.error ||
        data?.message ||
        error.message ||
        'Action failed';
      toast.error(msg);
    },
  });

  const runAction = async (actionValue) => {
    if (!actionValue || mutation.isPending) return;
    const keepY = captureWindowScroll();
    pinWindowScroll(keepY, 1200);
    setPendingValue(actionValue);
    try {
      const result = await mutation.mutateAsync(actionValue);
      if (result.kind === 'email') {
        toast.success(result.data?.message || 'Digest emailed — status Mail');
        invalidateDueQueries(queryClient, result.jobId);
        onMoved?.(result);
        return;
      }
      if (result.kind === 'schedule_ui') {
        onScheduleRequest?.();
        return;
      }
      if (result.kind === 'schedule') {
        toast.success(result.data?.message || 'Added to schedule');
        invalidateDueQueries(queryClient, result.jobId);
        onMoved?.(result);
        return;
      }
      if (result.kind === 'force') {
        const msg =
          result.forceValue === 'COMPLETED'
            ? 'Marked Completed — removed from this queue (still under Completed when you look there).'
            : `Updated to ${result.label}`;
        toast.success(msg);
        if (result.data?.bwof_message) toast.success(result.data.bwof_message);
        invalidateDueQueries(queryClient, result.jobId);
        onMoved?.(result);
      }
    } catch {
      /* toast in onError */
    } finally {
      setPendingValue('');
      pinWindowScroll(keepY, 800);
    }
  };

  const notifyActions = SS_ROW_ACTIONS.filter((a) => a.group === 'notify' || a.group === 'schedule');
  const moveActions = SS_ROW_ACTIONS.filter((a) => a.group === 'move');
  const allActions = [...notifyActions, ...moveActions];

  const btnBase = compact
    ? 'text-xs px-2 py-1 rounded border font-medium transition-colors disabled:opacity-50'
    : 'text-sm px-3 py-2 rounded-lg border font-medium transition-colors disabled:opacity-50';

  const btnIdle = compact
    ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
    : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50';

  const btnSelected = compact
    ? 'bg-amber-200 border-amber-500 text-amber-950 shadow-sm ring-1 ring-amber-400'
    : 'bg-amber-100 border-amber-500 text-amber-950 shadow-sm ring-2 ring-amber-300';

  const btnPending = 'opacity-70 cursor-wait';

  const buttons = (
    <div
      className={`inline-flex flex-wrap items-center gap-1.5 ${className}`}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="group"
      aria-label="SS actions"
    >
      {allActions.map((a) => {
        const isSelected = selected === a.value;
        const isPending = pendingValue === a.value && mutation.isPending;
        return (
          <button
            key={a.value}
            type="button"
            disabled={mutation.isPending}
            onMouseDown={preventFocusScroll}
            onClick={() => runAction(a.value)}
            title={a.label}
            className={`${btnBase} ${isSelected ? btnSelected : btnIdle} ${isPending ? btnPending : ''}`}
            aria-pressed={isSelected}
          >
            {isPending ? '…' : formatSsRowActionLabel(a)}
          </button>
        );
      })}
    </div>
  );

  if (compact) return buttons;

  return (
    <div className={`rounded-lg border border-amber-200 bg-amber-50/60 p-3 ${className}`}>
      <p className="text-sm font-medium text-gray-900 mb-1">SS actions</p>
      <p className="text-xs text-gray-600 mb-3">
        Tap a destination — it runs immediately. Shaded button matches the current status (including
        worker updates).
      </p>
      {buttons}
    </div>
  );
}
